import React, { useState, useEffect } from 'react';
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    IconButton, Tooltip, CircularProgress,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import { beautifyNumber } from '../../../Utils/FormatNumber';
import AssetService from '../../../Services/assets.service';
import AssetModalShell from './AssetModalShell/AssetModalShell';
import shell from './AssetModalShell/AssetModalShell.module.scss';

const SnapshotViewer = ({ open, onClose, caseId, token }) => {
    const { t, i18n } = useTranslation();
    const direction = i18n.dir();
    const [snapshots, setSnapshots] = useState([]);
    const [loading, setLoading] = useState(false);

    const fetchSnapshots = async () => {
        if (!caseId || !token) return;
        setLoading(true);
        try {
            const res = await AssetService.GetDepreciationSnapshots(caseId, token);
            setSnapshots(res?.data || []);
        } catch (err) {
            console.error('Failed to fetch snapshots:', err);
            setSnapshots([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (open) fetchSnapshots();
    }, [open]);

    const handleDelete = async (year) => {
        const result = await Swal.fire({
            title: t('DeleteSnapshotTitle') || 'מחיקת נתוני פחת',
            html: `<p>${t('DeleteSnapshotConfirm') || 'האם למחוק את נתוני הפחת לשנת'} <strong>${year}</strong>?</p>`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d32f2f',
            cancelButtonColor: '#999',
            confirmButtonText: t('Delete') || 'מחק',
            cancelButtonText: t('Cancel') || 'ביטול',
        });

        if (!result.isConfirmed) return;

        try {
            await AssetService.DeleteDepreciationSnapshot(caseId, year, token);
            await Swal.fire({
                title: t('Deleted') || 'נמחק',
                text: `${t('SnapshotDeletedFor') || 'נתוני הפחת נמחקו לשנת'} ${year}`,
                icon: 'success',
                confirmButtonColor: '#304FFF',
            });
            fetchSnapshots();
        } catch (err) {
            console.error('Delete snapshot error:', err);
            Swal.fire({
                title: t('Error') || 'שגיאה',
                text: t('FailedToDeleteSnapshot') || 'מחיקת נתוני הפחת נכשלה',
                icon: 'error',
                confirmButtonColor: '#d32f2f',
            });
        }
    };

    return (
        <AssetModalShell
            open={open}
            onClose={onClose}
            title={t('DepreciationSnapshots') || 'נתוני פחת שנשמרו'}
            direction={direction}
            size="md"
            closeOnBackdrop
            footer={(
                <button type="button" className={shell.secondaryBtn} onClick={onClose}>
                    {t('Close') || 'סגור'}
                </button>
            )}
        >
            {loading ? (
                <div className={shell.loading}><CircularProgress className={shell.spinner} /></div>
            ) : snapshots.length === 0 ? (
                <div className={shell.emptyState}>{t('NoSnapshots') || 'אין נתוני פחת שנשמרו'}</div>
            ) : (
                <div className={shell.tableWrap}>
                    <TableContainer>
                        <Table size="small" dir={direction}>
                            <TableHead>
                                <TableRow>
                                    <TableCell align="center">{t('Year') || 'שנה'}</TableCell>
                                    <TableCell align="center">{t('AssetsCount') || 'מספר נכסים'}</TableCell>
                                    <TableCell align="center">{t('AccumulatedDepreciation') || 'פחת שנצבר'}</TableCell>
                                    <TableCell align="center">{t('DepreciationThisYear') || 'פחת השנה'}</TableCell>
                                    <TableCell align="center">{t('LastUpdated') || 'עדכון אחרון'}</TableCell>
                                    <TableCell align="center" sx={{ width: 64 }}>{t('Actions') || 'פעולות'}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {snapshots.map((snap) => (
                                    <TableRow key={snap.year} hover>
                                        <TableCell align="center" sx={{ fontWeight: 700, color: 'var(--xtram-blue)' }}>{snap.year}</TableCell>
                                        <TableCell align="center">{snap.assetCount}</TableCell>
                                        <TableCell align="center">{beautifyNumber(snap.totalDepreciationUntilNow, 0)}</TableCell>
                                        <TableCell align="center" sx={{ fontWeight: 600 }}>{beautifyNumber(snap.totalDepreciationThisYear, 0)}</TableCell>
                                        <TableCell align="center" sx={{ color: 'var(--xtram-mirage-muted)', fontSize: 12 }}>
                                            {snap.lastUpdated ? new Date(snap.lastUpdated).toLocaleDateString('he-IL') : '—'}
                                        </TableCell>
                                        <TableCell align="center">
                                            <Tooltip title={t('DeleteSnapshot') || 'מחק נתוני פחת'}>
                                                <IconButton size="small" onClick={() => handleDelete(snap.year)} sx={{ color: '#c62828' }}>
                                                    <DeleteIcon sx={{ fontSize: 18 }} />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </div>
            )}
        </AssetModalShell>
    );
};

export default SnapshotViewer;
